/**
 * /policies/leave/accrual-rules — Session 3c PR 1.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { AccrualRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { showAlertTrace } from "./_session3aHelpers";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

function _statusTone(s: AccrualRuleItem["floor_status"]): string {
  if (s === "below") return "bg-rose-100 text-rose-800";
  if (s === "at_floor") return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

export function LeaveAccrualRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-leave-accrual-rules", le],
    queryFn: () => session3cApi.listAccrualRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => {
    const below = items.filter((r) => r.floor_status === "below").length;
    const at = items.filter((r) => r.floor_status === "at_floor").length;
    return { total: items.length, below, at };
  }, [items]);

  const columns: PolicyColumn<AccrualRuleItem>[] = [
    {
      headerKey: "policies.leave_accrual.col.leave_type",
      label: "Leave type",
      render: (r) => (
        <div>
          <div className="font-medium">{r.leave_type_name_en}</div>
          <div className="text-[10px] text-text-tertiary">{r.leave_type_code}</div>
        </div>
      ),
    },
    {
      headerKey: "policies.leave_accrual.col.method",
      label: "Calc method",
      render: (r) => <span className="text-xs text-text-secondary">{r.calc_method}</span>,
    },
    {
      headerKey: "policies.leave_accrual.col.rate",
      label: "Rate (days/month)",
      render: (r) => <span className="font-mono">{r.rate.value.toFixed(2)}</span>,
      traceableValue: (r) => ({ label: t("policies.leave_accrual.col.rate", "Rate"), stack: r.rate }),
    },
    {
      headerKey: "policies.leave_accrual.col.effective",
      label: "Effective from",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.effective_from}</span>,
    },
    {
      headerKey: "policies.leave_accrual.col.status",
      label: "Floor status",
      render: (r) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusTone(r.floor_status)}`}>
          {r.floor_status}
        </span>
      ),
    },
  ];

  return (
    <PolicyListPage<AccrualRuleItem>
      title={t("policies.leave_accrual.title", "Leave Accrual Rules")}
      subtitle={t(
        "policies.leave_accrual.subtitle",
        "Per-leave-type accrual rates. Mutaba'aa enforces Article 109 minimums on every save.",
      )}
      agent="mutabaa"
      domain="leave_accrual"
      newButtonLabel={t("policies.leave_accrual.cta.new", "New Accrual Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        counts.below > 0 ? {
          headline: t(
            "policies.leave_accrual.suggestion.below_headline",
            `${counts.below} rule${counts.below === 1 ? "" : "s"} BELOW Article 109 floor.`,
          ),
          body: t(
            "policies.leave_accrual.suggestion.below_body",
            "Mutaba'aa: immediate fix required. Any leave_request against these rules will be vetoed at save.",
          ),
          severity: "critical" as const,
          primaryActionLabel: t("policies.leave_accrual.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log?actor=mutabaa"),
          suggestionKey: "leave-accrual-below",
        } : {
          headline: t(
            "policies.leave_accrual.suggestion.clean_headline",
            `All ${items.length} accrual rules at or above Article 109 floor.`,
          ),
          body: t(
            "policies.leave_accrual.suggestion.clean_body",
            "Mutaba'aa cleared. Use the override-stack popover on any rate to see KSA floor → group → LE source.",
          ),
          primaryActionLabel: t("policies.leave_accrual.suggestion.view_first", "View first rule trace"),
          primaryActionOnClick: () => {
            if (items[0]) {
              showAlertTrace(
                `${items[0].leave_type_name_en} rate: ${items[0].rate.value.toFixed(2)} days/month` +
                  ` · KSA floor (Article 109): ${items[0].rate.ksa_floor ?? "—"}`,
              );
            }
          },
          suggestionKey: "leave-accrual-clean",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.at,
        critical: counts.below,
        top_kind: t("policies.leave_accrual.title", "Leave Accrual Rules"),
        window_label: t("policies.leave_accrual.window", "all leave types"),
      }}
    />
  );
}
